const content = `
# Email Deliverability & Suppressions (Phase 5)

When you share a demo, the invitation email goes through SendGrid. Most of
the time the recipient gets it within seconds and the row on /shares moves
from \`pending\` → \`sent\` → \`opened\` → \`viewed\`. But sometimes the email
bounces, is reported as spam, or the recipient explicitly unsubscribes — at
which point Eyvo Demo Agent moves the invitation into one of three new
terminal statuses, and the recipient's address goes onto the **suppression
list** so we don't try to deliver to them again by accident.

## The three new statuses

| Status | Meaning | What we do next |
|--------|---------|-----------------|
| **Suppressed** | The recipient is on the suppression list. We did **not** call SendGrid at all — the invitation row was created and immediately marked as suppressed. | The row is "delivery dead" until an admin overrides (see below) or the suppression is released. |
| **Undeliverable** | SendGrid attempted delivery, then reported a hard bounce or dropped the email. The mailbox almost certainly doesn't exist or rejected us at the SMTP layer. | We add the address to the suppression list and stop trying. |
| **Spam reported** | The recipient clicked "Report spam" in their mail client. | We add the address to the suppression list. Future sends to the same address will be blocked at pre-send. |

All three statuses show as **rose-coloured pills** on /shares. Hover the pill
to see the reason (the SendGrid bounce reason, or "Spam reported", etc.) — that
text is the \`undeliverable_reason\` field on the invitation row.

## How the suppression list gets populated

We listen to the **SendGrid Event Webhook**. Every time SendGrid emits a
\`bounce\`, \`dropped\`, \`spamreport\`, \`unsubscribe\` or \`group_unsubscribe\` event
for one of our outgoing emails, the handler:

1. Verifies the ECDSA-P256 signature on the payload.
2. Dedupes on \`sg_event_id\` (a webhook retry is a no-op).
3. Appends the raw event to the \`sendgrid_events\` ledger.
4. Looks up the matching invitation by \`sendgrid_message_id\`.
5. Transitions that invitation's status (\`undeliverable\` / \`spam_reported\`).
6. Adds (or upserts) a row to \`email_suppressions\` for that address.

After that, any new \`POST /api/demo-runs/{run_id}/invitations\` or resend that
targets the same address fails the pre-send check and returns
\`status: "suppressed"\` immediately, without consuming a SendGrid send.

## Manual override (admin only)

If you're certain a suppressed address is valid — for example, a customer
flagged us as spam by accident, or a bounce was caused by a one-off DNS
hiccup — admins can override the suppression on a per-submission basis:

1. Open the Share Video modal as you normally would.
2. Type or paste the suppressed email address. It will appear as a **rose chip
   with a shield icon** and the suppression reason inline.
3. An **"Override and send anyway"** toggle will appear (admins only). Flip
   it on. The submit button now sends the request with
   \`override_suppression: true\`.
4. The send fires through SendGrid. We log a \`suppression_overridden\` row
   into the audit timeline that captures **who** overrode the suppression,
   **when**, and the original \`reason\` / \`source\` / \`added_at\` of the
   suppression so the decision is fully traceable.

The override is **per-submission** — it does NOT release the suppression. The
next attempt without the override flag will be blocked again.

## Releasing a suppression (currently manual)

There isn't a UI for "release this suppression and let normal sends through"
yet — that's Phase 5.1 work. For now, an admin with Mongo access can clear
the suppression by setting \`released_at\` and \`released_by\` on the
\`email_suppressions\` row:

\`\`\`js
db.email_suppressions.updateOne(
  { email: "person@example.com" },
  { $set: { released_at: new Date(), released_by: "admin@eyvo.com" } }
);
\`\`\`

After that the address falls back through the pre-send check and the next
invite is delivered normally.

## What you can do today

- **Filter /shares by the new statuses** to see who's currently
  suppressed / undeliverable / spam-reported. Use the status dropdown.
- **Hover the pill** to see the precise reason without leaving the table.
- **Export the filtered set as CSV** with the "Export CSV" button next to
  "More filters" — useful for handing the list to ops or for an external
  review.
- If you're an admin and a recipient is legitimately blocked by mistake,
  use the override toggle on the Share modal as described above. The audit
  record is everything CSAT will need to defend the decision later.
`;

export default content;
